const MAX_MB = 8;
export const CUSTOMER_PHOTO_MAX_COUNT = 6;
export const CUSTOMER_PHOTO_ACCEPT =
  'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif';

function fileLooksLikeAllowedPhoto(file) {
  const type = String(file?.type || '').toLowerCase();
  if (['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(type)) {
    return true;
  }
  const name = String(file?.name || '').toLowerCase();
  return /\.(jpe?g|png|webp|gif)$/i.test(name);
}

async function readPhotoMagicKind(file) {
  try {
    const buf = await file.slice(0, 16).arrayBuffer();
    const b = new Uint8Array(buf);
    if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
    if (
      b.length >= 8 &&
      b[0] === 0x89 &&
      b[1] === 0x50 &&
      b[2] === 0x4e &&
      b[3] === 0x47
    ) {
      return 'png';
    }
    if (b.length >= 6 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return 'gif';
    if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) {
      const tag = String.fromCharCode(b[8], b[9], b[10], b[11]);
      if (tag === 'WEBP') return 'webp';
    }
    if (b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
      const brand = String.fromCharCode(b[8], b[9], b[10], b[11]).toLowerCase();
      if (['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(brand)) return 'heic';
      if (brand === 'avif') return 'avif';
    }
  } catch {
    /* ignore */
  }
  return '';
}

/** @returns {Promise<{ ok: true, url: string } | { ok: false, error: string }>} */
export async function uploadCustomerPhotoFile(file) {
  if (!file) {
    return { ok: false, error: 'No photo selected.' };
  }
  if (file.size > MAX_MB * 1024 * 1024) {
    return { ok: false, error: `Photo must be under ${MAX_MB} MB.` };
  }
  if (!fileLooksLikeAllowedPhoto(file)) {
    const magic = await readPhotoMagicKind(file);
    if (magic === 'heic' || magic === 'avif') {
      return {
        ok: false,
        error: 'This photo is HEIC/AVIF. Save or export it as JPG or PNG, then upload again.',
      };
    }
    if (!['jpeg', 'png', 'gif', 'webp'].includes(magic)) {
      return { ok: false, error: 'Use JPG, PNG, WebP, or GIF.' };
    }
  }

  try {
    const fd = new FormData();
    fd.append('photo', file);
    const r = await fetch('/api/customer_upload.php', { method: 'POST', body: fd });
    const d = await r.json();
    if (!r.ok || !d.ok || !d.url) {
      return { ok: false, error: d.error || 'Upload failed. Try again.' };
    }
    return { ok: true, url: String(d.url) };
  } catch {
    return { ok: false, error: 'Upload failed. Check your connection.' };
  }
}

/** Parse stored dress/reference field — single URL or JSON array. */
export function parseBespokePhotos(stored) {
  const raw = String(stored || '').trim();
  if (!raw) return [];
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((u) => String(u || '').trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
  }
  return [raw];
}

/** Serialize for API — single URL or JSON array when multiple. */
export function serializeBespokePhotos(urls) {
  const list = (Array.isArray(urls) ? urls : [])
    .map((u) => String(u || '').trim())
    .filter(Boolean)
    .slice(0, CUSTOMER_PHOTO_MAX_COUNT);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return JSON.stringify(list);
}
